/**
 * Hook for managing user activity timeline filter state
 * Simplified version of dashboard filters for single-user context
 */

import { useState, useCallback, useMemo, useEffect } from 'react'
import dayjs from 'dayjs'
import type {
  RosterUserTimelineFilters,
  RosterUserTimelineQueryParams,
  RosterActivityTypeOption,
} from '../types'

/**
 * localStorage key for toggle preference
 */
const TOGGLE_STORAGE_KEY = 'user-timeline-license-credential-toggle'

/**
 * Get saved toggle preference from localStorage
 */
function getSavedTogglePreference(): boolean {
  try {
    let saved = localStorage.getItem(TOGGLE_STORAGE_KEY)
    return saved ? JSON.parse(saved) : false
  } catch {
    return false
  }
}

/**
 * Save toggle preference to localStorage
 */
function saveTogglePreference(value: boolean): void {
  try {
    localStorage.setItem(TOGGLE_STORAGE_KEY, JSON.stringify(value))
  } catch {
    // Ignore localStorage errors (e.g., in private mode)
  }
}

/**
 * Initial filter state for user timeline
 */
let initialFilters: RosterUserTimelineFilters = {
  timeRange: 'month',
  customStartDate: dayjs().subtract(1, 'month').toISOString(),
  customEndDate: dayjs().toISOString(),
  searchKeyword: '',
  selectedCategories: [],
  selectedActivityTypes: [],
  showLicenseCredentialEvents: getSavedTogglePreference(),
}

/**
 * Convert filter state to API query parameters
 */
function filtersToQueryParams(
  filters: RosterUserTimelineFilters,
): RosterUserTimelineQueryParams {
  let { customStartDate, customEndDate, timeRange } = filters

  // Calculate start/end dates based on timeRange
  let startDate = (() => {
    if (timeRange === 'custom' && customStartDate) {
      return dayjs(customStartDate).format('YYYY-MM-DD')
    }

    let now = dayjs()
    switch (timeRange) {
      case 'week':
        return now.subtract(1, 'week').format('YYYY-MM-DD')
      case 'month':
        return now.subtract(1, 'month').format('YYYY-MM-DD')
      case 'quarter':
        return now.subtract(3, 'months').format('YYYY-MM-DD')
      case 'year':
        return now.subtract(1, 'year').format('YYYY-MM-DD')
      default:
        return now.subtract(1, 'month').format('YYYY-MM-DD')
    }
  })()

  let endDate = (() => {
    if (timeRange === 'custom' && customEndDate) {
      return dayjs(customEndDate).format('YYYY-MM-DD')
    }
    return dayjs().format('YYYY-MM-DD')
  })()

  return {
    startDate,
    endDate,
    searchKeyword: filters.searchKeyword.trim() || undefined,
    categories:
      filters.selectedCategories.length > 0
        ? filters.selectedCategories
        : undefined,
    activityTypes:
      filters.selectedActivityTypes.length > 0
        ? filters.selectedActivityTypes
        : undefined,
    showLicenseCredentialEvents: filters.showLicenseCredentialEvents,
  }
}

/**
 * Activity type options for user timeline - matching Manage Dashboard
 */
export let getActivityTypeOptions = (
  showLicenseCredentialEvents: boolean,
): RosterActivityTypeOption[] => {
  let baseOptions = [
    { value: 'ce-activity', label: 'CE Activity' },
    { value: 'assignment', label: 'Assignment' },
    { value: 'user-action', label: 'User Action' },
  ]

  let licenseCredentialOptions = [
    { value: 'license-activity', label: 'License Activity' },
    { value: 'credential-activity', label: 'Credential Activity' },
  ]

  return showLicenseCredentialEvents
    ? [...baseOptions, ...licenseCredentialOptions]
    : baseOptions
}

/**
 * Course categories for filtering
 */
export let courseCategories = [
  'Airway/Respiratory/Ventilation',
  'Cardiovascular',
  'Trauma',
  'Medical',
  'Pediatrics',
  'Operations',
  'Critical Care',
  'Other',
]

/**
 * Hook for managing user timeline filters
 */
export function useUserTimelineFilters() {
  let [filters, setFilters] =
    useState<RosterUserTimelineFilters>(initialFilters)

  // Save toggle preference to localStorage whenever it changes
  useEffect(() => {
    saveTogglePreference(filters.showLicenseCredentialEvents)
  }, [filters.showLicenseCredentialEvents])

  // Update individual filter values
  let updateFilter = useCallback(
    <K extends keyof RosterUserTimelineFilters>(
      key: K,
      value: RosterUserTimelineFilters[K],
    ) => {
      setFilters((prev) => ({
        ...prev,
        [key]: value,
      }))
    },
    [],
  )

  // Reset all filters to defaults (but preserve toggle preference)
  let resetFilters = useCallback(() => {
    setFilters((prev) => ({
      ...initialFilters,
      showLicenseCredentialEvents: prev.showLicenseCredentialEvents, // Preserve toggle state
    }))
  }, [])

  // Convert current filters to query parameters (memoized for stable references)
  let queryParams = useMemo(() => filtersToQueryParams(filters), [filters])

  // Get activity type options based on current toggle state
  let activityTypeOptions = useMemo(
    () => getActivityTypeOptions(filters.showLicenseCredentialEvents),
    [filters.showLicenseCredentialEvents],
  )

  // Progressive filter logic - only show CE filters when CE activity is selected
  let shouldShowCEFilters = useMemo(
    () =>
      filters.selectedActivityTypes.length === 0 ||
      filters.selectedActivityTypes.includes('ce-activity'),
    [filters.selectedActivityTypes],
  )

  // Clear CE-specific filters when they should be hidden
  useEffect(() => {
    if (!shouldShowCEFilters) {
      if (filters.selectedCategories.length > 0) {
        setFilters((prev) => ({
          ...prev,
          selectedCategories: [],
        }))
      }
    }
  }, [shouldShowCEFilters, filters.selectedCategories.length])

  // Check if any filters are active (excluding date range)
  let hasActiveFilters = useMemo(
    () =>
      filters.searchKeyword.trim().length > 0 ||
      filters.selectedCategories.length > 0 ||
      filters.selectedActivityTypes.length > 0,
    [
      filters.searchKeyword,
      filters.selectedCategories.length,
      filters.selectedActivityTypes.length,
    ],
  )

  return {
    filters,
    queryParams,
    updateFilter,
    resetFilters,
    activityTypeOptions,
    shouldShowCEFilters,
    hasActiveFilters,
    setFilters,
  }
}
